import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * Helper to perform a fetch with ETag caching support.
 * Returns the full AxiosResponse object.
 *
 * @param axiosInstance The axios instance to use for the request.
 * @param url The URL to fetch (relative to axiosInstance baseURL).
 * @param config Axios request config (params, etc.).
 * @param cacheKeyBase A unique key for this resource type (e.g., 'usersModule').
 *                     The final key will include query params to ensure uniqueness.
 */
export async function fetchWithEtagSafe<T>(
  axiosInstance: AxiosInstance,
  url: string,
  config: AxiosRequestConfig = {},
  cacheKeyBase: string,
): Promise<AxiosResponse<T>> {
  // Sort keys in params to ensure consistent cache key
  const sortedParams = config.params ? sortObjectKeys(config.params) : {};

  const fullCacheKey = `etag_cache:${cacheKeyBase}:${url}:${JSON.stringify(
    sortedParams,
  )}`;
  const etagKey = `${fullCacheKey}:etag`;
  const dataKey = `${fullCacheKey}:data`;

  const cachedEtag = localStorage.getItem(etagKey);
  const cachedData = localStorage.getItem(dataKey);

  const headers = { ...config.headers };

  // Only send If-None-Match if we have both etag and data
  if (cachedEtag && cachedData) {
    headers["If-None-Match"] = cachedEtag;
  }

  const response = await axiosInstance.get(url, {
    ...config,
    headers,
    validateStatus: (status) =>
      (status >= 200 && status < 300) || status === 304,
  });

  if (response.status === 304) {
    // We are guaranteed to have cachedData if we sent the header
    return {
      ...response,
      data: JSON.parse(cachedData!) as T,
      status: 200,
    };
  }

  // 200 or other 2xx
  const newEtag = response.headers["etag"];
  if (newEtag) {
    localStorage.setItem(etagKey, newEtag);
    localStorage.setItem(dataKey, JSON.stringify(response.data));
  } else {
    localStorage.removeItem(etagKey);
    localStorage.removeItem(dataKey);
  }

  return response;
}

/**
 * Xoá cache ETag trong localStorage cho một resource (theo `cacheKeyBase`), hoặc TOÀN BỘ nếu bỏ
 * trống. Dùng ngay sau khi GHI dữ liệu (vd thêm thành viên vào nhóm người xử lý) để lần fetch kế
 * tiếp của resource đó không nhận 304 (bản cũ) mà lấy bản mới từ server.
 */
export function clearEtagCache(cacheKeyBase?: string): void {
  try {
    const prefix = cacheKeyBase
      ? `etag_cache:${cacheKeyBase}:`
      : "etag_cache:";
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage không khả dụng (private mode…) → bỏ qua, không chặn luồng.
  }
}

function sortObjectKeys(obj: any): any {
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
    return obj;
  }
  return Object.keys(obj)
    .sort()
    .reduce((result: any, key) => {
      result[key] = sortObjectKeys(obj[key]);
      return result;
    }, {});
}
